// src/components/layout/DashboardLayout.jsx
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import {
  useNotification,
  GlobalNotificationContainer,
} from '@/components/ui/NotificationContainer';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';
import logoPsiando from '@/assets/icon/logo_psiando.jpeg';

// =============================================================================
// LOGO
// =============================================================================
const Logo = ({ size = 32 }) => {
  const dim = typeof size === 'string' ? parseInt(size, 10) : size;
  return (
    <img
      src={logoPsiando}
      alt="Psiando"
      width={dim}
      height={dim}
      style={{ width: dim, height: dim, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }}
    />
  );
};

// =============================================================================
// UTILITÁRIOS PUROS — fora do componente (sem stale closure)
// =============================================================================
const hasAccessToRoute = (path, userProfile) => {
  if (path.includes('/agenda-digital/') && !userProfile?.canCreateAgenda)   return false;
  if (path.includes('/supervisao/')     && !userProfile?.canSeekSupervision) return false;
  if (path.includes('/instituicao/')    && userProfile?.user_type !== 'instituicao') return false;
  if (path.includes('/supervisor/')     && userProfile?.user_type !== 'supervisor')  return false;
  return true;
};

const getUserTypeLabel = (userType) => {
  const labels = {
    psicologo:     'Psicólogo',
    supervisor:    'Supervisor',
    instituicao:   'Instituição',
    internacional: 'Internacional',
  };
  return labels[userType] || 'Usuário';
};

const getThemeClass = (userType) => {
  const themes = {
    psicologo:     'theme-psicologo',
    supervisor:    'theme-supervisor',
    instituicao:   'theme-instituicao',
    internacional: 'theme-internacional',
  };
  return themes[userType] || 'theme-psicologo';
};

const getDashboardSection = (path) => {
  if (path.includes('/agenda-digital/')) return 'agenda-digital';
  if (path.includes('/supervisao/'))     return 'supervisao';
  if (path.includes('/instituicao/'))    return 'instituicao';
  if (path.includes('/supervisor/'))     return 'supervisor';
  if (path.includes('/perfil'))          return 'perfil';
  if (path.includes('/configuracoes'))   return 'configuracoes';
  if (path.includes('/suporte'))         return 'suporte';
  return 'overview';
};

const generateBreadcrumbs = (path) => {
  const crumbs = [{ label: 'Dashboard', path: '/dashboard' }];
  if (path.includes('/agenda-digital/')) {
    crumbs.push({ label: 'Agenda Digital', path: '/dashboard/agenda-digital/gerenciar' });
    if (path.includes('/criar'))      crumbs.push({ label: 'Criar Agenda' });
    if (path.includes('/configurar')) crumbs.push({ label: 'Configurações' });
  }
  if (path.includes('/supervisao/')) {
    crumbs.push({ label: 'Supervisão', path: '/dashboard/supervisao/minhas' });
    if (path.includes('/buscar'))   crumbs.push({ label: 'Buscar Supervisor' });
    if (path.includes('/agendar'))  crumbs.push({ label: 'Agendar Sessão' });
    if (path.includes('/matching')) crumbs.push({ label: 'Sugestões IA' });
  }
  if (path.includes('/perfil'))        crumbs.push({ label: 'Perfil' });
  if (path.includes('/configuracoes')) crumbs.push({ label: 'Configurações' });
  return crumbs;
};

const getDashboardMenuItems = (userProfile) => {
  const items = [{ id: 'overview', label: 'Visão Geral', path: '/dashboard', icon: '📊' }];
  if (userProfile?.canCreateAgenda)
    items.push({ id: 'agenda-digital', label: 'Agenda Digital', path: '/dashboard/agenda-digital/gerenciar', icon: '📅' });
  if (userProfile?.canSeekSupervision)
    items.push({ id: 'supervisao', label: 'Supervisões', path: '/dashboard/supervisao/minhas', icon: '🎓' });
  if (userProfile?.user_type === 'supervisor')
    items.push({ id: 'supervisor', label: 'Supervisionados', path: '/dashboard/supervisor/supervisionados', icon: '👥' });
  if (userProfile?.user_type === 'instituicao')
    items.push({ id: 'instituicao', label: 'Profissionais', path: '/dashboard/instituicao/profissionais', icon: '🏢' });
  items.push(
    { id: 'perfil',        label: 'Perfil',        path: '/dashboard/perfil',        icon: '👤' },
    { id: 'configuracoes', label: 'Configurações',  path: '/dashboard/configuracoes', icon: '⚙️' }
  );
  return items;
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated, isLoading } = useAuth();
  const {
    navigation,
    setSidebarOpen,
    setActiveSection,
    updateBreadcrumbs,
    isLoading: isAppLoading,
  } = useApp();

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Rotas /admin/* têm seu próprio sistema de autenticação (adminService).
    // Inicializa o layout diretamente sem verificar AuthContext.
    if (location.pathname.startsWith('/admin')) {
      setIsInitialized(true);
      return;
    }
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/auth/login', { state: { from: location.pathname }, replace: true });
      return;
    }
    setIsInitialized(true);
  }, [isLoading, isAuthenticated, navigate, location.pathname]);

  useEffect(() => {
    if (!isInitialized) return;
    setActiveSection(getDashboardSection(location.pathname));
    updateBreadcrumbs(generateBreadcrumbs(location.pathname));
  }, [location.pathname, isInitialized, setActiveSection, updateBreadcrumbs]);

  useEffect(() => {
    if (userProfile?.user_type) {
      const themeClass = getThemeClass(userProfile.user_type);
      document.body.className = document.body.className.replace(/theme-\w+/g, '');
      document.body.classList.add(themeClass, 'dashboard-mode');
    }
    return () => {
      document.body.classList.remove('dashboard-mode');
      document.body.className = document.body.className.replace(/theme-\w+/g, '');
    };
  }, [userProfile]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  useEffect(() => {
    // Rotas /admin/* não passam pela verificação de acesso do DashboardLayout
    if (location.pathname.startsWith('/admin')) return;
    if (isInitialized && !hasAccessToRoute(location.pathname, userProfile)) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, userProfile, isInitialized, navigate]);

  // Rotas /admin/* não dependem de user/userProfile do AuthContext
  if (!isInitialized || (!location.pathname.startsWith('/admin') && (!user || !userProfile))) {
    return <LoadingOverlay message="Carregando dashboard..." />;
  }

  const sidebarOpen = navigation?.sidebarOpen ?? false;

  return (
    <div className="min-h-screen dashboard-layout" style={{ backgroundColor: 'var(--background-light)' }}>
      <DashboardHeader
        user={user}
        userProfile={userProfile}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="flex flex-1 pt-16">
        <DashboardSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userProfile={userProfile}
          activeSection={navigation?.activeSection ?? 'overview'}
        />

        <div
          className={`flex-1 transition-all duration-300 ease-in-out min-h-screen ${
            sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'
          }`}
        >
          {(navigation?.breadcrumbs?.length ?? 0) > 0 && (
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <Breadcrumb items={navigation.breadcrumbs} />
            </div>
          )}
          <main className="flex-1">
            <div className="container-dashboard">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* CORREÇÃO: usa GlobalNotificationContainer que consome o NotificationContext
          compartilhado em vez de receber `notifications` do AppContext (que foi removido). */}
      <GlobalNotificationContainer position="top-right" />

      {isAppLoading && <LoadingOverlay />}
    </div>
  );
};

// =============================================================================
// DASHBOARD HEADER
// =============================================================================
const DashboardHeader = ({ user, userProfile, sidebarOpen, onToggleSidebar }) => {
  const { logout } = useAuth();
  // CORREÇÃO: showNotification não existe no AppContext — usar showSuccess/showError
  // do NotificationContext via useNotification()
  const { showSuccess, showError } = useNotification();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      showSuccess('Logout realizado com sucesso');
    } catch (error) {
      console.error('Erro no logout:', error);
      showError('Erro ao fazer logout');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 h-16">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {sidebarOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          <div className="flex items-center gap-3">
            <Logo size={32} />
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-gray-900">Dashboard</h1>
              <span className="text-xs text-gray-500 capitalize">
                {getUserTypeLabel(userProfile?.user_type)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <NetworkStatus />
          <NotificationBell />

          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 transition-colors"
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
            >
              <UserAvatar user={user} size="sm" />
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900 truncate max-w-32">
                  {user.user_metadata?.name ?? user.user_metadata?.full_name ?? user.email ?? 'Usuário'}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {getUserTypeLabel(userProfile?.user_type)}
                </p>
              </div>
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {userMenuOpen && (
              <UserDropdownMenu
                user={user}
                userProfile={userProfile}
                onClose={() => setUserMenuOpen(false)}
                onLogout={handleLogout}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// =============================================================================
// SIDEBAR
// =============================================================================
const DashboardSidebar = ({ isOpen, onClose, userProfile, activeSection }) => {
  const menuItems = getDashboardMenuItems(userProfile);
  return (
    <>
      {/* Desktop */}
      <aside
        className={`fixed left-0 top-16 bottom-0 z-30 transition-all duration-300 ease-in-out bg-white border-r border-gray-200 hidden lg:flex flex-col ${
          isOpen ? 'w-64' : 'w-16'
        }`}
      >
        <SidebarContent items={menuItems} activeSection={activeSection} isCollapsed={!isOpen} />
      </aside>

      {/* Mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col pt-16 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent
          items={menuItems}
          activeSection={activeSection}
          isCollapsed={false}
          onItemClick={onClose}
        />
      </aside>
    </>
  );
};

const SidebarContent = ({ items, activeSection, isCollapsed, onItemClick }) => {
  const navigate = useNavigate();
  const handleItemClick = (item) => {
    if (item.path) { navigate(item.path); onItemClick?.(); }
  };
  return (
    <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto" aria-label="Menu do dashboard">
      {items.map((item) => (
        <SidebarMenuItem
          key={item.id}
          item={item}
          isActive={activeSection === item.id}
          isCollapsed={isCollapsed}
          onClick={() => handleItemClick(item)}
        />
      ))}
    </nav>
  );
};

const SidebarMenuItem = ({ item, isActive, isCollapsed, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      isCollapsed ? 'justify-center' : 'justify-start'
    }`}
    style={
      isActive
        ? {
            backgroundColor: 'var(--primary-50)',
            color: 'var(--primary-accent)',
            borderRight: '2px solid var(--primary-accent)',
          }
        : { color: '#374151' }
    }
    title={isCollapsed ? item.label : undefined}
    aria-current={isActive ? 'page' : undefined}
  >
    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">{item.icon}</span>
    {!isCollapsed && <span className="ml-3 truncate">{item.label}</span>}
    {!isCollapsed && item.badge && (
      <span
        className="ml-auto text-xs px-2 py-0.5 rounded-full"
        style={{ backgroundColor: 'var(--secondary-light)', color: 'var(--primary-dark)' }}
      >
        {item.badge}
      </span>
    )}
  </button>
);

// =============================================================================
// COMPONENTES AUXILIARES
// =============================================================================
const UserAvatar = ({ user, size = 'md' }) => {
  const sizeClasses = { sm: 'w-8 h-8 text-sm', md: 'w-10 h-10 text-base', lg: 'w-12 h-12 text-lg' };
  const initials = (
    user.user_metadata?.name ?? user.user_metadata?.full_name ?? user.email ?? 'U'
  ).charAt(0).toUpperCase();

  return (
    <div
      className={`${sizeClasses[size] ?? sizeClasses.md} rounded-full flex items-center justify-center flex-shrink-0`}
      style={{ backgroundColor: 'var(--primary-main)' }}
    >
      <span className="text-white font-medium">{initials}</span>
    </div>
  );
};

const UserDropdownMenu = ({ user, userProfile, onClose, onLogout }) => {
  const navigate = useNavigate();
  const displayName =
    user.user_metadata?.name ?? user.user_metadata?.full_name ?? user.email ?? 'Usuário';

  const menuItems = [
    { label: 'Perfil',        path: '/dashboard/perfil',        icon: '👤' },
    { label: 'Configurações', path: '/dashboard/configuracoes', icon: '⚙️' },
  ];

  return (
    <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50">
      <div className="py-1">
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
          <p className="text-xs text-gray-500 truncate">{user.email}</p>
          <p className="text-xs capitalize font-medium" style={{ color: 'var(--primary-accent)' }}>
            {getUserTypeLabel(userProfile?.user_type)}
          </p>
        </div>

        {menuItems.map((item) => (
          <button
            key={item.label}
            onClick={() => { navigate(item.path); onClose(); }}
            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <span className="mr-3">{item.icon}</span>
            {item.label}
          </button>
        ))}

        <div className="border-t border-gray-200">
          <button
            onClick={onLogout}
            className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <span className="mr-3">🚪</span>
            Sair
          </button>
        </div>
      </div>
    </div>
  );
};

const NetworkStatus = () => {
  const { isOnline } = useApp();
  if (isOnline !== false) return null;
  return (
    <div className="flex items-center text-red-600" title="Sem conexão com a internet">
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M18.364 5.636L5.636 18.364M5.636 5.636l12.728 12.728" />
      </svg>
    </div>
  );
};

// CORREÇÃO: unreadCount removido do AppContext — NotificationBell agora usa
// o NotificationContext para contar notificações não lidas.
const NotificationBell = () => {
  const { notifications } = useNotification();
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <button
      className="relative p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
      aria-label={`Notificações${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ''}`}
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
};

export default DashboardLayout;
